// Phase 5 · Guardian Vault UI.
// Edit the governed banks for a product: allowed C-n, forbidden F-n,
// forbidden patterns, voice rules. Every save writes an audit row.
import React, { useCallback, useEffect, useMemo, useState } from "react";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

const TABS = [
  { key: "allowed", label: "Allowed (C-n)",
    help: "Facts you can say. Each must have id, text." },
  { key: "forbidden", label: "Forbidden (F-n)",
    help: "Facts you must not say. Each must have id, never_say, why." },
  { key: "forbidden-patterns", label: "Forbidden patterns",
    help: "Deterministic regexes. Each must have id, pattern, severity, forbidden_claim_id." },
  { key: "voice-rules", label: "Voice rules",
    help: "Short editorial rules the generator reads at draft time." },
];

export default function GuardianVaultUI() {
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [tab, setTab] = useState("allowed");
  const [bank, setBank] = useState(null);
  const [raw, setRaw] = useState("");
  const [state, setState] = useState({ loading: true, error: null });
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [flash, setFlash] = useState(null);
  const [audit, setAudit] = useState([]);

  // Load products once.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/products");
        setProducts(r.data?.products || []);
        if (!productId && (r.data?.products || []).length) {
          setProductId(r.data.products[0].id);
        }
      } catch (e) {
        setState((s) => ({ ...s, error: formatApiError(e) }));
      }
    })();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Load the bank for the selected product.
  const load = useCallback(async () => {
    if (!productId) return;
    setState({ loading: true, error: null });
    try {
      const [bRes, aRes] = await Promise.all([
        api.get(`/vault/claim-bank/${productId}`),
        api.get(`/vault/audit?limit=40`),
      ]);
      setBank(bRes.data);
      setAudit(aRes.data?.rows || []);
      const v = bRes.data?.banks?.[tab]?.value || [];
      setRaw(JSON.stringify(v, null, 2));
      setState({ loading: false, error: null });
    } catch (e) {
      setState({ loading: false, error: formatApiError(e) });
    }
  }, [productId, tab]);
  useEffect(() => { load(); }, [load]);

  const parsed = useMemo(() => {
    try { return { ok: true, value: JSON.parse(raw) }; }
    catch (e) { return { ok: false, err: String(e.message || e) }; }
  }, [raw]);

  async function save() {
    if (!parsed.ok) { setFlash(`Invalid JSON: ${parsed.err}`); return; }
    if (!Array.isArray(parsed.value)) { setFlash("Value must be a JSON array."); return; }
    setBusy(true);
    try {
      await api.put(`/vault/claim-bank/${productId}/${tab}`, {
        value: parsed.value, reason: reason || null,
      });
      setFlash("Saved · audit row written. Guardian will pick up on next evaluate call.");
      setReason("");
      load();
    } catch (e) {
      setFlash(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell title="Guardian Vault">
      <div className="bo-page" data-testid="vault-ui">
        <section className="bo-card">
          <div className="bo-eyebrow">Phase 5 · Vault</div>
          <h1 className="bo-serif" style={{ fontSize: 26, margin: "6px 0 8px" }}>
            Guardian Vault
          </h1>
          <p style={{ color: "var(--bo-muted)", maxWidth: 780, lineHeight: 1.55 }}>
            Edit the governed claim banks + voice rules Guardian enforces at
            draft time. Every save supersedes the previous APPROVED statement
            and writes an audit entry (actor, before/after, reason).
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12, alignItems: "center" }}>
            <label style={{ fontSize: 13 }}>
              Product:{" "}
              <select
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid var(--bo-border)" }}
                data-testid="vault-product-select"
              >
                {products.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} · {p.slug}</option>
                ))}
              </select>
            </label>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {TABS.map((t) => (
                <button
                  key={t.key}
                  className={`bo-btn ${tab === t.key ? "bo-btn-primary" : "bo-btn-ghost"}`}
                  onClick={() => setTab(t.key)}
                  data-testid={`vault-tab-${t.key}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        <ErrorBanner error={state.error} />
        {flash && (
          <div className="bo-flash" style={{ marginTop: 12, padding: 10, borderRadius: 8, background: "var(--bo-accent-soft)" }}>
            {flash}
          </div>
        )}

        {state.loading ? <LoadingCard label="Loading…" /> : !bank ? (
          <EmptyState title="Pick a product" description="Select a product above to edit its Guardian Vault." />
        ) : (
          <section className="bo-card" style={{ marginTop: 20 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <strong>{TABS.find((t) => t.key === tab)?.label}</strong>
              <Pill tone="muted">
                items: {Array.isArray(bank.banks[tab]?.value) ? bank.banks[tab].value.length : 0}
              </Pill>
              <Pill tone={parsed.ok ? "success" : "danger"}>
                JSON {parsed.ok ? "ok" : "invalid"}
              </Pill>
              <div style={{ marginLeft: "auto", fontSize: 12, color: "var(--bo-muted)" }}>
                {TABS.find((t) => t.key === tab)?.help}
              </div>
            </div>
            <textarea
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              rows={22}
              className="bo-mono"
              style={{
                marginTop: 10, width: "100%", padding: 10, borderRadius: 8,
                border: "1px solid var(--bo-border)", background: "var(--bo-bg-elev)",
                color: "var(--bo-ink)", fontSize: 12, lineHeight: 1.55,
              }}
              data-testid={`vault-raw-${tab}`}
            />
            <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
              <input
                type="text"
                placeholder="reason (recorded in audit log)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                style={{ flex: "1 1 300px", padding: "8px 10px", borderRadius: 6, border: "1px solid var(--bo-border)" }}
                data-testid="vault-reason"
              />
              <button className="bo-btn bo-btn-primary" onClick={save} disabled={busy || !parsed.ok} data-testid="vault-save">
                {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </section>
        )}

        <section className="bo-card" style={{ marginTop: 20 }}>
          <strong>Recent Vault edits</strong>
          <div style={{ marginTop: 8, display: "grid", gap: 6 }}>
            {audit.length === 0 ? (
              <div style={{ color: "var(--bo-muted)", fontSize: 13 }}>No audit entries yet.</div>
            ) : audit.map((r) => (
              <div key={r.id} className="bo-mono" style={{ fontSize: 11, color: "var(--bo-muted)" }}>
                [{new Date(r.created_at).toLocaleString()}] {r.actor_user_id?.slice(0,8)} · {r.action} · {r.field_path || r.target_id || "—"} · {r.reason || ""}
              </div>
            ))}
          </div>
        </section>
      </div>
    </Shell>
  );
}
